import { AnchorEntry, CanvasPosition, Entry, EntityType, NoteEntry, ReferenceEntry } from '../types';

export const ANCHOR_SNAP_THRESHOLD = 36;
export const ANCHOR_SNAP_EPSILON = 1;
const ANCHOR_AXIS_PADDING = 200;
const ANCHOR_AXIS_ACTIVE_PADDING = 100000;
const DRAG_SLIDE_TILT_STEP = 30;

/** Entries that can be pinned to an anchor line via `anchorId`. */
export type SnappableEntry = ReferenceEntry | NoteEntry;

function isSnappable(entry: Entry): entry is SnappableEntry {
	return entry.type === EntityType.Reference || entry.type === EntityType.Note;
}

export type AnchorLineSpec = {
  anchorId: string;
  repositoryId: string;
  /** Canvas reference entry this anchor is attached to, or null for a free anchor. */
  referenceId: string | null;
  direction: 'vertical' | 'horizontal';
  /** Fixed axis coordinate: x for a vertical line, y for a horizontal line. */
  coord: number;
  /** Start of the line extent in the varying direction (anchor position). */
  min: number;
  /** End of the line extent (last snapped ref + padding). */
  max: number;
  /** True when a matching reference is being dragged — line pulses. */
  isActive: boolean;
};

export function buildAnchorLineSpecs(args: {
  anchorEntries: Array<{ id: string; anchor: AnchorEntry; position: CanvasPosition }>;
  snappedWithPos: Array<{ entry: SnappableEntry; x: number; y: number }>;
  draggingRepositoryId: string | null;
  /** True while dragging a note — notes aren't restricted to one collection, so every line is a candidate. */
  isDraggingNote: boolean;
  epsilon: number;
}): AnchorLineSpec[] {
	const { anchorEntries, snappedWithPos, draggingRepositoryId, isDraggingNote, epsilon } = args;

	return anchorEntries
		.filter(({ anchor }) => Boolean(anchor.repositoryId))
		.map(({ id, anchor, position }) => {
			const repositoryId = anchor.repositoryId as string;
			const direction: 'vertical' | 'horizontal' =
        anchor.direction === 'horizontal' ? 'horizontal' : 'vertical';
			const referenceId = anchor.referenceId ?? null;
			const isActive = isDraggingNote || draggingRepositoryId === repositoryId;
			// Notes have no repositoryId, so entries already pinned to this line are matched by anchorId directly.
			const attached = snappedWithPos.filter((s) => s.entry.anchorId === id);

			if (direction === 'vertical') {
				const snappedYs = attached
					.filter((s) => Math.abs(s.x - position.x) <= epsilon)
					.map((s) => s.y);
				const min = position.y;
				const max = isActive
					? position.y + ANCHOR_AXIS_ACTIVE_PADDING
					: snappedYs.length > 0
						? Math.max(...snappedYs)
						: position.y + ANCHOR_AXIS_PADDING;
				return { anchorId: id, repositoryId, referenceId, direction, coord: position.x, min, max, isActive };
			}

			const snappedXs = attached
				.filter((s) => Math.abs(s.y - position.y) <= epsilon)
				.map((s) => s.x);
			const min = position.x;
			const max = isActive
				? position.x + ANCHOR_AXIS_ACTIVE_PADDING
				: snappedXs.length > 0
					? Math.max(...snappedXs)
					: position.x + ANCHOR_AXIS_PADDING;
			return { anchorId: id, repositoryId, referenceId, direction, coord: position.y, min, max, isActive };
		});
}

/** Returns the anchor line `entry` is sitting on (within epsilon), or null. */
function findSnappedLine(
	entry: Entry,
	position: CanvasPosition,
	lines: AnchorLineSpec[],
	epsilon: number,
): AnchorLineSpec | null {
	if (!isSnappable(entry)) return null;
	// References only snap onto a line for their own collection; notes can pin to any line.
	const restrictedRepositoryId = entry.type === EntityType.Reference ? (entry as ReferenceEntry).repositoryId : null;
	for (const line of lines) {
		if (restrictedRepositoryId !== null && restrictedRepositoryId !== line.repositoryId) continue;
		const isOnLine =
			line.direction === 'vertical'
				? Math.abs(position.x - line.coord) <= epsilon
				: Math.abs(position.y - line.coord) <= epsilon;
		if (isOnLine) return line;
	}
	return null;
}

/** Returns the anchorId of the anchor line `entry` is sitting on (within epsilon), or null. */
export function getSnappedAnchorId(
	entry: Entry,
	position: CanvasPosition,
	lines: AnchorLineSpec[],
	epsilon: number,
): string | null {
	return findSnappedLine(entry, position, lines, epsilon)?.anchorId ?? null;
}

/** Rotation (deg) for a card sliding along a fixed line, alternating every `DRAG_SLIDE_TILT_STEP` px of `along` so the card shakes as it slides. */
export function getDragSlideTilt(along: number): number {
	return Math.floor(along / DRAG_SLIDE_TILT_STEP) % 2 === 0 ? 2 : 0;
}

/** `getDragSlideTilt` for a card dragged along an anchor line. 0 when not on a line or when the entry hosts anchor lines of its own. */
export function getAnchorDragTilt(
	entry: Entry,
	position: CanvasPosition,
	lines: AnchorLineSpec[],
	epsilon: number,
): number {
	if (lines.some((l) => l.referenceId === entry.id)) return 0;
	const line = findSnappedLine(entry, position, lines, epsilon);
	if (!line) return 0;
	return getDragSlideTilt(line.direction === 'vertical' ? position.y : position.x);
}

/** Returns a snapped position if `entry` is eligible for any anchor line, otherwise the original position. */
export function applyAnchorSnap(
	entry: Entry,
	position: CanvasPosition,
	lines: AnchorLineSpec[],
	threshold: number,
): CanvasPosition {
	if (!isSnappable(entry)) return position;
	// References only snap onto a line for their own collection; notes can pin to any line.
	const restrictedRepositoryId = entry.type === EntityType.Reference ? (entry as ReferenceEntry).repositoryId : null;

	for (const line of lines) {
		if (restrictedRepositoryId !== null && restrictedRepositoryId !== line.repositoryId) continue;

		if (line.direction === 'vertical') {
			if (
				Math.abs(position.x - line.coord) <= threshold &&
        position.y >= line.min &&
        position.y <= line.max
			) {
				return { x: line.coord, y: position.y };
			}
		} else {
			if (
				Math.abs(position.y - line.coord) <= threshold &&
        position.x >= line.min &&
        position.x <= line.max
			) {
				return { x: position.x, y: line.coord };
			}
		}
	}
	return position;
}
